import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { clampMetaDescription } from '@/lib/seoDescription';
import { enforceBrandSuffixLimit, formatPageTitle, getExactPageTitle } from '@/lib/seoTitle';

/** JSON.stringify for an inline <script>: escape <, >, & and line separators so data cannot close the tag. */
export function serializeJsonLd(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/**
 * v7 SEO Component - Meta tags and structured data
 *
 * Per UX Spec v7.1 and Epic 17:
 * - Story 17.3: SEO Meta Tags & Structured Data
 * - React Helmet for meta tags
 * - Open Graph tags for social sharing
 * - Twitter card tags
 * - JSON-LD structured data
 */

interface SEOProps {
  title?: string;
  titleAbsolute?: boolean;
  /** Overrides og:title and twitter:title only (e.g. a shared item); <title> stays `title`. */
  socialTitle?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'event' | 'profile';
  noIndex?: boolean;
  structuredData?: object | object[];
  keywords?: string;
  // Event-specific
  eventStartDate?: string;
  eventEndDate?: string;
  eventLocation?: string;
  // Article-specific
  publishedTime?: string;
  author?: string;
}

export const DEFAULT_TITLE = 'Journal Party: Guided Journal Sessions for Self-Discovery';
const DEFAULT_DESCRIPTION =
  'Guided prompts, timers and music for pen & paper journalers, plus mental health programs built with therapists. Build a journaling habit you actually keep.';
export const SITE_URL = 'https://journalparty.com';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/api/og-image`;

/**
 * Known og:image sizes. Generated OG cards (/api/og*, /og/*) are 1200x630.
 * Other images (e.g. article hero photos) vary, so no size is declared for them.
 */
export function getOgImageSize(image: string | undefined): { width: number; height: number } | null {
  if (!image) return null;
  const path = image.replace(/^https?:\/\/[^/]+/, '');
  if (path.startsWith('/api/og') || path.startsWith('/og/')) return { width: 1200, height: 630 };
  return null;
}

/** True when the page already passed an Event block, so the auto one would duplicate it. */
function structuredDataHasEvent(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(structuredDataHasEvent);
  if (!value || typeof value !== 'object') return false;
  const record = value as { '@type'?: unknown; '@graph'?: unknown };
  const type = record['@type'];
  if (type === 'Event' || (Array.isArray(type) && type.includes('Event'))) return true;
  return structuredDataHasEvent(record['@graph']);
}

export function SEO({
  title,
  titleAbsolute = false,
  socialTitle,
  description: rawDescription = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  url,
  type = 'website',
  noIndex = false,
  structuredData,
  keywords,
  eventStartDate,
  eventEndDate,
  eventLocation,
  publishedTime,
  author,
}: SEOProps) {
  const description = clampMetaDescription(rawDescription);
  const ogImageSize = getOgImageSize(image);
  // Spec titles for specific paths win; otherwise the suffix rule applies:
  // " | Journal Party" only when the result stays within 60 characters.
  const exactTitle = title ? getExactPageTitle(url) : null;
  const fullTitle = exactTitle
    ?? (title
      ? (titleAbsolute ? enforceBrandSuffixLimit(title) : formatPageTitle(title))
      : DEFAULT_TITLE);
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;

  // Base Organization structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Journal Party',
    url: SITE_URL,
    logo: `${SITE_URL}/icons/logo-512x512.png`,
    sameAs: [
      'https://twitter.com/journalpartyomg',
      'https://instagram.com/journalpartyomg',
      'https://youtube.com/@journalparty',
    ],
    description: DEFAULT_DESCRIPTION,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: `${SITE_URL}/contact`,
    },
  };

  // WebSite structured data with search action
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'Journal Party',
    url: SITE_URL,
    publisher: { '@id': `${SITE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/discover?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  // Event structured data. Skip this thin block when the page already
  // passed an Event (the event page passes the one that includes an offer).
  const eventSchema = eventStartDate && !structuredDataHasEvent(structuredData)
    ? {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: title,
        description: description,
        startDate: eventStartDate,
        endDate: eventEndDate,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        location: {
          '@type': 'VirtualLocation',
          url: fullUrl,
          name: eventLocation || 'Journal Party Online',
        },
        organizer: {
          '@type': 'Organization',
          name: 'Journal Party',
          url: SITE_URL,
        },
        image: fullImage,
      }
    : null;

  // Combine all structured data
  const pageStructuredData = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  const allStructuredData = [
    organizationSchema,
    websiteSchema,
    ...(eventSchema ? [eventSchema] : []),
    ...pageStructuredData,
  ];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={socialTitle ?? fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      {ogImageSize && <meta property="og:image:width" content={String(ogImageSize.width)} />}
      {ogImageSize && <meta property="og:image:height" content={String(ogImageSize.height)} />}
      <meta property="og:site_name" content="Journal Party" />

      {/* Article-specific OG tags */}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {author && <meta property="article:author" content={author} />}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={socialTitle ?? fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      <meta property="twitter:site" content="@journalpartyomg" />

      {/* Event-specific meta */}
      {eventStartDate && (
        <>
          <meta property="event:start_time" content={eventStartDate} />
          {eventEndDate && (
            <meta property="event:end_time" content={eventEndDate} />
          )}
        </>
      )}

      {/* Structured Data */}
      {allStructuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {serializeJsonLd(data)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * SEO presets for common pages
 */
export const SEOPresets = {
  homepage: {
    title: DEFAULT_TITLE,
    titleAbsolute: true,
    description: DEFAULT_DESCRIPTION,
    url: '/',
  },
  discover: {
    title: 'Discover Events',
    description:
      'Find live journaling sessions happening now or coming soon. Join a supportive community of writers.',
  },
  calendar: {
    title: 'Event Calendar',
    description:
      'Browse upcoming journaling events and plan your writing sessions. Never miss a live event.',
  },
  pricing: {
    title: 'Pricing',
    description:
      'Journal Party Premium: Hundreds of programs with new releases every week, and daily prompts for journaling. $97 a year after 14 days free, or $12 a month after 7 days free.',
  },
  profile: {
    title: 'My Profile',
    description: 'View your journaling stats, streaks, and achievements.',
  },
} as const;

export default SEO;
